package com.tovtalk.ui.platform

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.onPlaced
import kotlinx.coroutines.isActive
import androidx.compose.ui.layout.positionInRoot
import com.tovtalk.data.cache.MediaCacheManager
import com.tovtalk.utils.viewportHeightPx
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLVideoElement
import org.w3c.dom.events.Event

private const val WEB_VIDEO_LAYER_ID = "tovtalk-web-video-layer"

/** Last placement so we can re-apply on visualViewport resize/scroll (mobile address bar, keyboard). */
private var lastVideoPlacement: Pair<HTMLVideoElement, kotlin.Pair<Float, Float>>? = null
private var lastVideoSize: Pair<Int, Int>? = null

/** Guard: register the video-layer viewport listeners exactly once (container is a page-lifetime singleton). */
private var videoViewportListenersRegistered = false
/** rAF id to coalesce reposition work to one per frame. */
private var videoRepositionRafId: Int = 0

private fun reapplyWebVideoPosition() {
    val placement = lastVideoPlacement ?: return
    val size = lastVideoSize ?: return
    positionWebVideoElement(placement.first, placement.second.first, placement.second.second, size.first, size.second)
}

private fun scheduleWebVideoReposition(container: org.w3c.dom.Element) {
    if (videoRepositionRafId != 0) return
    videoRepositionRafId = window.requestAnimationFrame { _ ->
        videoRepositionRafId = 0
        container.asDynamic().style.maxHeight = "${viewportHeightPx() - BOTTOM_NAV_HEIGHT_PX}px"
        reapplyWebVideoPosition()
    }
}

@Composable
actual fun TovTalkVideoPlayer(
    videoUrl: String?,
    thumbnailUrl: String?,
    modifier: Modifier,
    isMuted: Boolean,
    isPlaying: Boolean,
    playbackRate: Float,
    loop: Boolean,
    onTap: (() -> Unit)?,
    onVideoEnded: (() -> Unit)?,
    aspectRatio: Float,
    fillContainer: Boolean,
    nextVideoUrl: String?,
    onVideoReady: (() -> Unit)?,
    allSources: Map<String, String>,
    onPositionUpdate: ((positionMs: Long, durationMs: Long) -> Unit)?,
    onVideoLooped: (() -> Unit)?,
) {
    val videoElement = remember { getOrCreateWebVideoElement() }

    var resolvedVideoUrl by remember { mutableStateOf<String?>(null) }
    var resolvedThumbnailUrl by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(videoUrl) {
        resolvedVideoUrl = videoUrl?.takeIf { it.isNotBlank() }?.let {
            try { MediaCacheManager.getVideoUrl(it) } catch (_: Throwable) { it }
        }
    }

    LaunchedEffect(thumbnailUrl) {
        resolvedThumbnailUrl = thumbnailUrl?.takeIf { it.isNotBlank() }?.let {
            try { MediaCacheManager.getImageUrl(it) } catch (_: Throwable) { it }
        }
    }

    DisposableEffect(resolvedVideoUrl, isPlaying, isMuted, playbackRate, loop, onVideoEnded, resolvedThumbnailUrl) {
        videoElement.onended = if (onVideoEnded != null) ({ _ -> onVideoEnded() }) else null
        videoElement.onplaying = if (onVideoReady != null) ({ _ -> onVideoReady() }) else null
        if (!resolvedThumbnailUrl.isNullOrBlank()) {
            videoElement.setAttribute("poster", resolvedThumbnailUrl!!)
        } else {
            videoElement.removeAttribute("poster")
        }
        if (!resolvedVideoUrl.isNullOrBlank()) {
            if (videoElement.src != resolvedVideoUrl) {
                videoElement.src = resolvedVideoUrl!!
                videoElement.load()
            }
            videoElement.muted = isMuted
            videoElement.loop = loop
            videoElement.playbackRate = playbackRate.toDouble()
            if (isPlaying) {
                videoElement.play()?.catch { }
            } else {
                videoElement.pause()
            }
        } else {
            videoElement.pause()
            videoElement.removeAttribute("src")
            videoElement.parentElement?.asDynamic()?.style?.display = "none"
        }
        onDispose { }
    }

    // C8: position polling for analytics
    LaunchedEffect(isPlaying, resolvedVideoUrl, onPositionUpdate, onVideoLooped) {
        if (!isPlaying || resolvedVideoUrl == null || onPositionUpdate == null) return@LaunchedEffect
        var lastPos = 0L
        while (isActive) {
            kotlinx.coroutines.delay(500)
            val pos = (videoElement.currentTime * 1000).toLong()
            val durRaw = videoElement.duration
            val dur = if (durRaw.isNaN() || durRaw.isInfinite()) 0L else (durRaw * 1000).toLong()
            onPositionUpdate.invoke(pos, dur)
            if (lastPos >= (dur - 500L) && pos <= 500L && dur > 0L) {
                onVideoLooped?.invoke()
            }
            lastPos = pos
        }
    }

    Box(
        modifier = (if (fillContainer) modifier else modifier.aspectRatio(aspectRatio))
            .background(Color.Transparent)
            .onPlaced { coordinates ->
                val size = coordinates.size
                val pos = coordinates.positionInRoot()
                lastVideoPlacement = videoElement to (pos.x to pos.y)
                lastVideoSize = size.width to size.height
                positionWebVideoElement(videoElement, pos.x, pos.y, size.width, size.height)
                if (!videoUrl.isNullOrBlank()) {
                    videoElement.parentElement?.asDynamic()?.style?.display = "block"
                }
            }
    )
}

// Must match TovTalkSpacing.bottomNavHeight / DesignTokens.Spacing.bottomNavHeight (64)
private const val BOTTOM_NAV_HEIGHT_PX = 64

private fun getOrCreateWebVideoElement(): HTMLVideoElement {
    var container = document.getElementById(WEB_VIDEO_LAYER_ID)
    if (container == null) {
        container = document.createElement("div")
        container.id = WEB_VIDEO_LAYER_ID
        val s = container.asDynamic().style
        s.position = "fixed"
        s.left = "0px"
        s.top = "0px"
        s.width = "0px"
        s.height = "0px"
        s.maxHeight = "${viewportHeightPx() - BOTTOM_NAV_HEIGHT_PX}px"
        s.overflow = "hidden"
        s.pointerEvents = "none"
        s.zIndex = "2"
        document.body!!.appendChild(container)
        if (!videoViewportListenersRegistered) {
            videoViewportListenersRegistered = true
            val onResize: (Event) -> Unit = { scheduleWebVideoReposition(container) }
            window.addEventListener("resize", onResize)
            val vv = window.asDynamic().visualViewport
            if (vv != null) {
                vv.addEventListener("resize", onResize)
                vv.addEventListener("scroll", onResize)
            }
        }
    }
    var video = container.querySelector("video") as? HTMLVideoElement
    if (video == null) {
        video = document.createElement("video") as HTMLVideoElement
        video.setAttribute("playsinline", "true")
        video.setAttribute("webkit-playsinline", "true")
        video.muted = true
        video.style.setProperty("object-fit", "contain")
        video.style.setProperty("width", "100%")
        video.style.setProperty("height", "100%")
        video.addEventListener("click", { e ->
            e.stopPropagation()
            e.preventDefault()
        })
        container.appendChild(video)
    }
    return video
}

private fun positionWebVideoElement(video: HTMLVideoElement, x: Float, y: Float, width: Int, height: Int) {
    val container = video.parentElement ?: return
    val s = container.asDynamic().style
    val viewportHeight = viewportHeightPx()
    val maxBottomY = viewportHeight - BOTTOM_NAV_HEIGHT_PX
    // Compose Canvas renders into a <canvas> element. Try Canvas first, then fall back to DOM root.
    val canvasEl = document.querySelector("canvas") ?: document.getElementById("root")?.firstElementChild
    val (leftPx, topPx) = if (canvasEl != null && canvasEl is org.w3c.dom.HTMLElement) {
        val rect = canvasEl.getBoundingClientRect()
        Pair(rect.left + x, rect.top + y)
    } else {
        Pair(x.toDouble(), y.toDouble())
    }
    // Ensure video never overlaps bottom nav: limit height so bottom edge stays above nav
    val allowedHeight = (maxBottomY - topPx).toInt().coerceAtLeast(1)
    val clampedHeight = minOf(height, (viewportHeight - BOTTOM_NAV_HEIGHT_PX).coerceAtLeast(1), allowedHeight)
    s.left = "${leftPx}px"
    s.top = "${topPx}px"
    s.width = "${width}px"
    s.height = "${clampedHeight}px"
    s.maxHeight = "${maxBottomY}px"
    s.display = "block"
}
